/**
 * BuildStamp
 * ----------
 * The version line at the foot of the sidebar. It answers one question a
 * human otherwise cannot answer from a browser: *which build am I looking
 * at?*
 *
 * WHY IT READS TWO SOURCES
 *
 * ``frontend-build`` (the nginx image serving this JS) and ``web`` (the
 * Django image serving the API) are SEPARATE images. One build produces both,
 * but the deploy pulls them by separate digests and recreates them as
 * separate containers, so they can end up running different code. On
 * 2026-09-06 (deployment d-L3RC058AK) exactly that happened — a stale digest
 * shipped five-day-old code — and CodeDeploy reported Succeeded throughout.
 *
 * So:
 *   * ``frontendBuild.gitSha`` is inlined in this bundle by Vite. It always
 *     works, including when the API is down, and it is the only value that
 *     describes the JS actually executing.
 *   * ``/api/version/`` reports the sha baked into the running web IMAGE
 *     (not what the deploy intended — see server/build_stamp.py).
 *
 * When both are known and differ, the badge says so. That mismatch is the
 * signal; agreeing shas are just a version number. Nothing here blocks,
 * retries or alarms — this cycle only makes the fact visible.
 *
 * DEGRADATION: a local ``vite dev`` run has no VITE_GIT_SHA and an older web
 * image has no ``/api/version/`` route. Either way this renders whatever it
 * does know and never an error; when it knows nothing at all it renders
 * nothing, because "unknown · unknown" is noise, not data.
 */
import React from "react";

import {
  UNKNOWN,
  frontendBuild,
  shasDisagree,
  shortSha,
} from "@/lib/buildInfo";
import { useBuildVersionQuery } from "@/queries/versionQueries";
import { useMeQuery } from "@/queries/queries";
import { cn } from "@/lib/utils";

interface BuildStampProps {
  // No `variant` prop. A "rail" variant existed for the Classic SidebarNav's
  // collapsed rail; unmounting that shell (tracker-only scope) deleted its
  // only caller, leaving three uncovered branches and a prop nobody passed.
  // Reinstate it with a caller and a test, never speculatively.
  className?: string;
}

const BuildStamp: React.FC<BuildStampProps> = ({ className }) => {
  // STAFF ONLY (Leo, 2026-09-07). Everything this renders is an operator's
  // instrument: a commit sha, a channel word, and a warning about OUR deploy
  // being internally inconsistent. A customer can act on none of it, and the
  // mismatch state would hand them a red alarm about our infrastructure on
  // precisely the day we are already having one. The API stays open — the
  // deploy scripts and a 3am triage read it with no credentials, and that
  // posture is argued in server/views.py — this gates only the CHROME.
  //
  // Hooks stay unconditional and in a fixed order (same rule the sidebars
  // pin): both queries are called on every render, and the gate is applied
  // to the OUTPUT, never by skipping a hook.
  const { data: user } = useMeQuery();
  const isStaff = !!user?.isStaff;

  // Failure is expected and uninteresting here (404 on an older web image,
  // offline, logged out), so the error branch is simply "no backend half".
  // ``enabled`` keeps a customer session from issuing the request at all.
  const { data } = useBuildVersionQuery({ enabled: isStaff });

  const feSha = frontendBuild.gitSha;
  const beSha = data?.gitSha ?? UNKNOWN;
  // Channel is a property of the deploy, not of one image, so either source
  // can supply it. Prefer the bundle's: it needs no network.
  const channel =
    frontendBuild.channel !== UNKNOWN
      ? frontendBuild.channel
      : (data?.channel ?? UNKNOWN);

  const displaySha = feSha !== UNKNOWN ? shortSha(feSha) : shortSha(beSha);
  if (!isStaff) {
    // Not our audience. Checked AFTER every hook, before anything renders.
    return null;
  }
  if (displaySha === UNKNOWN && channel === UNKNOWN) {
    // Nothing to say. A local dev session lands here.
    return null;
  }

  const mismatch = shasDisagree(feSha, beSha);

  // The hover title carries the long form so the visible line stays one
  // short string. This is the text someone pastes into an incident thread.
  const title = [
    `frontend ${feSha}`,
    `backend  ${beSha}`,
    data?.builtAt ? `built    ${data.builtAt}` : null,
    data?.source ? `stamp    ${data.source}` : null,
    mismatch
      ? "MISMATCH: the page and the API are running different builds."
      : null,
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <div
      title={title}
      className={cn(
        "font-mono text-[10px] leading-tight tracking-[0.3px]",
        mismatch ? "text-destructive" : "text-muted-foreground",
        "px-3",
        className,
      )}
    >
      {/* aria-label repeats the mismatch in words: the colour alone is not
          an accessible signal, and this line is read by whoever is on call. */}
      <span
        aria-label={
          mismatch
            ? `Build ${displaySha}; the page and the API are running different builds`
            : `Build ${displaySha}`
        }
      >
        {channel !== UNKNOWN ? `${channel} · ` : null}
        {displaySha}
        {mismatch ? " ⚠" : null}
      </span>
    </div>
  );
};

export default BuildStamp;
